import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import toast from "react-hot-toast";
import axios from "axios";

import { AccountDetailsQueryKeys } from "@/modules/pipeline/account-details/data/keys";
import { useApiClient } from "@/context/api-client";
import { TIME_INTERVALS } from "shared/lib/constants/time";
import { LookalikeI } from "@/lib/types/lookalike";

export const useFetchCampaignScript = (campaignId: string | undefined) => {
  const API = useApiClient();

  const campaignScriptApi = useQuery({
    enabled: !!campaignId,
    queryKey: AccountDetailsQueryKeys.script,
    queryFn: () => API.getCampaignV2(campaignId as string),
    select: (data) => data?.data.campaign.script || "",
  });

  useEffect(() => {
    if (campaignScriptApi.isError) {
      toast.error("Error fetching script.");
    }
  }, [campaignScriptApi.isError]);

  return campaignScriptApi;
};

export const useFetchCampaignDefaultScript = (
  campaignId: string | undefined
) => {
  const API = useApiClient();

  const campaignDefaultScriptApi = useQuery({
    enabled: !!campaignId,
    queryKey: AccountDetailsQueryKeys.defaultScript,
    queryFn: () => API.getCampaignDefaultScript(campaignId as string),
    select: (data) => data?.data.script_text || "",
  });

  useEffect(() => {
    if (campaignDefaultScriptApi.isError) {
      toast.error("Error fetching script.");
    }
  }, [campaignDefaultScriptApi.isError]);

  return campaignDefaultScriptApi;
};

export const useFetchCampaignIcpTitles = (campaignId: string | undefined) => {
  const API = useApiClient();

  const campaignIcpTitlesApi = useQuery({
    enabled: !!campaignId,
    queryKey: AccountDetailsQueryKeys.icpTitles,
    queryFn: () => API.getCampaignIcpTitles(campaignId as string),
    select: (data) => data?.data.icp_titles || [],
  });

  useEffect(() => {
    if (campaignIcpTitlesApi.isError) {
      toast.error("Error fetching campaign ICP titles.");
    }
  }, [campaignIcpTitlesApi.isError]);

  return campaignIcpTitlesApi;
};

export const useFetchCampaignCustomDispositions = (
  campaignId: string | undefined
) => {
  const API = useApiClient();

  return useQuery({
    enabled: !!campaignId,
    queryKey: AccountDetailsQueryKeys.customDispositions,
    queryFn: () => API.getCampaignCustomDispositions(campaignId as string),
    select: (data) => data?.data.dispositions || [],
  });
};

export const useFetchLookalike = (campaignId: string | undefined) => {
  const API = useApiClient();

  return useQuery({
    enabled: !!campaignId,
    queryKey: AccountDetailsQueryKeys.lookalike(campaignId || ""),
    staleTime: TIME_INTERVALS.FIVE_MINUTES,
    retry: false,
    queryFn: async (): Promise<LookalikeI | null> => {
      const response = await API.getLookalike(campaignId as string);

      if (axios.isAxiosError(response) || response?.status !== 200) {
        return null;
      }

      return response.data;
    },
  });
};

export const useQueryFetchBookedMeetingCalendarEventForAccount = (
  campaignId: string | undefined,
  accountId: string | undefined
) => {
  const apiClient = useApiClient();

  const query = useQuery({
    enabled: !!campaignId || !!accountId,
    queryKey: AccountDetailsQueryKeys.bookedMeetingCalendarEvent(
      campaignId || "",
      accountId || ""
    ),
    staleTime: TIME_INTERVALS.ONE_SECOND * 30,
    queryFn: async () => {
      const response =
        await apiClient.bookedMeetingCalendarEvents.getForAccount(
          campaignId || "",
          accountId || ""
        );

      if (response.status !== 200) {
        throw new Error(
          `Failed to fetch booked meeting calendar event for account: ${accountId} (campaign: ${campaignId})`
        );
      }

      return response.data.event;
    },
  });

  useEffect(() => {
    if (query.isError) {
      toast.error(
        query.error.message || "Failed to fetch booked meeting calendar event."
      );
    }
  }, [query.isError]);

  return query;
};
